import type { CampaignPriority, CabinetMember, DelegationRule, EopOffice, NomineeProfile } from '../types/simulation.js';

export const CAMPAIGN_PRIORITIES: CampaignPriority[] = [
  { id: 'priority-economy', label: 'Croissance et coût de la vie', domain: 'economie', intensity: 3 },
  { id: 'priority-industry', label: 'Industrie et chaînes d’approvisionnement', domain: 'entreprises', intensity: 2 },
  { id: 'priority-security', label: 'Sécurité nationale', domain: 'defense', intensity: 3 },
  { id: 'priority-diplomacy', label: 'Diplomatie et alliances', domain: 'monde', intensity: 2 },
  { id: 'priority-infrastructure', label: 'Infrastructures', domain: 'infrastructure', intensity: 2 },
  { id: 'priority-health', label: 'Santé publique', domain: 'societe', intensity: 2 },
  { id: 'priority-education', label: 'Éducation', domain: 'societe', intensity: 2 },
  { id: 'priority-immigration', label: 'Immigration', domain: 'societe', intensity: 2 },
  { id: 'priority-energy', label: 'Énergie et résilience du réseau', domain: 'infrastructure', intensity: 2 },
  { id: 'priority-tech', label: 'Technologie, IA et concurrence', domain: 'entreprises', intensity: 2 }
];

const cabinet = (departmentId: string, departmentName: string, title: string): CabinetMember => ({
  id: `cabinet-${departmentId}`,
  departmentId,
  departmentName,
  title,
  actorId: `cabinet-actor-${departmentId}`,
  name: `Responsable intérimaire — ${departmentName}`,
  status: 'acting',
  competence: 66,
  management: 64,
  congressRelations: 52,
  institutionalLoyalty: 82,
  publicReputation: 48,
  priorities: ['Continuité administrative', 'Transition des dossiers'],
  leadershipStyle: { riskTolerance:45, diplomacyBias:50, militaryBias:50, fiscalDiscipline:60, technologyPriority:55, administrativeEffectiveness:64, doctrine:'Pragmatisme institutionnel et continuité' },
  senateConfirmationRequired: true
});

export const INITIAL_CABINET: CabinetMember[] = [
  cabinet('state', 'Département d’État', 'Secretary of State'),
  cabinet('treasury', 'Département du Trésor', 'Secretary of the Treasury'),
  cabinet('defense', 'Département de la Défense', 'Secretary of Defense'),
  cabinet('justice', 'Département de la Justice', 'Attorney General'),
  cabinet('interior', 'Département de l’Intérieur', 'Secretary of the Interior'),
  cabinet('agriculture', 'Département de l’Agriculture', 'Secretary of Agriculture'),
  cabinet('commerce', 'Département du Commerce', 'Secretary of Commerce'),
  cabinet('labor', 'Département du Travail', 'Secretary of Labor'),
  cabinet('hhs', 'Santé et Services sociaux', 'Secretary of Health and Human Services'),
  cabinet('hud', 'Logement et Développement urbain', 'Secretary of Housing and Urban Development'),
  cabinet('transportation', 'Département des Transports', 'Secretary of Transportation'),
  cabinet('energy', 'Département de l’Énergie', 'Secretary of Energy'),
  cabinet('education', 'Département de l’Éducation', 'Secretary of Education'),
  cabinet('va', 'Anciens Combattants', 'Secretary of Veterans Affairs'),
  cabinet('dhs', 'Sécurité intérieure', 'Secretary of Homeland Security')
];

export const INITIAL_EOP: EopOffice[] = [
  { id: 'eop-cos', name: 'White House Office', leaderRole: 'Chief of Staff', leaderName: 'Jordan Ellis', quality: 76, capacity: 78, responsibilities: ['Coordination présidentielle', 'Agenda', 'Arbitrage interagences'] },
  { id: 'eop-omb', name: 'Office of Management and Budget', leaderRole: 'Director of OMB', leaderName: 'À nommer', quality: 70, capacity: 74, responsibilities: ['Budget', 'Réglementation', 'Performance fédérale'] },
  { id: 'eop-nsc', name: 'National Security Council', leaderRole: 'National Security Advisor', leaderName: 'Taylor Brooks', quality: 74, capacity: 76, responsibilities: ['Sécurité nationale', 'Coordination interagences', 'Crises'] },
  { id: 'eop-nec', name: 'National Economic Council', leaderRole: 'Director of NEC', leaderName: 'Morgan Lee', quality: 71, capacity: 72, responsibilities: ['Politique économique', 'Coordination domestique'] },
  { id: 'eop-dpc', name: 'Domestic Policy Council', leaderRole: 'Director of DPC', leaderName: 'Casey Rivera', quality: 70, capacity: 70, responsibilities: ['Politique intérieure', 'Coordination sociale'] },
  { id: 'eop-ustr', name: 'Office of the U.S. Trade Representative', leaderRole: 'U.S. Trade Representative', leaderName: 'À nommer', quality: 69, capacity: 68, responsibilities: ['Commerce extérieur', 'Négociations commerciales'] },
  { id: 'eop-comms', name: 'White House Communications', leaderRole: 'Communications Director', leaderName: 'Avery Morgan', quality: 73, capacity: 74, responsibilities: ['Message public', 'Presse', 'Coordination des annonces'] }
];

export const INITIAL_DELEGATION: DelegationRule[] = [
  { category: 'presidence', mode: 'APPROVAL' },
  { category: 'gouvernement', mode: 'NOTIFY' },
  { category: 'institutions', mode: 'APPROVAL' },
  { category: 'economie', mode: 'NOTIFY' },
  { category: 'etats-unis', mode: 'NOTIFY' },
  { category: 'defense', mode: 'APPROVAL' },
  { category: 'monde', mode: 'APPROVAL' },
  { category: 'infrastructure', mode: 'AUTO' },
  { category: 'societe', mode: 'NOTIFY' },
  { category: 'entreprises', mode: 'NOTIFY' },
  { category: 'opinion', mode: 'NOTIFY' },
  { category: 'donnees', mode: 'AUTO' },
  { category: 'systeme', mode: 'AUTO' }
];

const nominee = (id: string, name: string, departmentId: string, scores: [number, number, number, number, number, number], conflicts: string[] = []): NomineeProfile => ({
  id, name, departmentId, party: 'independant', competence: scores[0], management: scores[1], congressRelations: scores[2], institutionalLoyalty: scores[3], publicReputation: scores[4], estimatedSenateSupport: scores[5], conflicts,
  leadershipStyle:{riskTolerance:Math.max(25,Math.min(85,45+(scores[0]-70)*.6)),diplomacyBias:departmentId==='state'?82:departmentId==='defense'?42:58,militaryBias:departmentId==='defense'?82:departmentId==='state'?28:45,fiscalDiscipline:departmentId==='treasury'?84:58,technologyPriority:['commerce','energy','defense'].includes(departmentId)?76:55,administrativeEffectiveness:scores[1],doctrine:departmentId==='state'?'Alliance, négociation et influence':departmentId==='defense'?'Readiness, dissuasion et modernisation':departmentId==='treasury'?'Stabilité, discipline budgétaire et marchés':departmentId==='justice'?'Indépendance des poursuites et État de droit':'Exécution administrative et coordination interagences'}
});

export const NOMINEE_POOL: NomineeProfile[] = [
  nominee('nom-state-1','Dr. Elena Ward','state',[84,72,73,78,67,58]),
  nominee('nom-state-2','Marcus Reed','state',[74,80,66,71,75,54]),
  nominee('nom-treasury-1','Dr. Naomi Chen','treasury',[88,76,69,82,63,61]),
  nominee('nom-treasury-2','Samuel Price','treasury',[77,84,72,70,71,56]),
  nominee('nom-defense-1','Adm. (ret.) Daniel Cross','defense',[86,79,64,85,72,57]),
  nominee('nom-defense-2','Dr. Maya Foster','defense',[82,82,71,79,68,60]),
  nominee('nom-justice-1','Rachel Monroe','justice',[85,74,62,88,65,55]),
  nominee('nom-justice-2','Thomas Hale','justice',[76,78,74,76,70,59]),
  nominee('nom-energy-1','Dr. Priya Shah','energy',[89,73,67,81,62,63]),
  nominee('nom-transportation-1','Luis Bennett','transportation',[80,86,70,78,66,60]),
  nominee('nom-commerce-1','Nora Fields','commerce',[78,81,75,69,73,58]),
  nominee('nom-dhs-1','Alexis Grant','dhs',[81,80,61,84,64,54]),
  nominee('nom-interior-1','Morgan Hayes','interior',[78,79,64,80,62,57]),
  nominee('nom-agriculture-1','Cameron Fields','agriculture',[80,82,68,77,66,59]),
  nominee('nom-labor-1','Jordan Alvarez','labor',[79,80,72,76,70,58]),
  nominee('nom-hhs-1','Dr. Evelyn Brooks','hhs',[88,82,67,82,69,60]),
  nominee('nom-hud-1','Parker Lewis','hud',[76,81,70,74,67,57]),
  nominee('nom-education-1','Dr. Riley Morgan','education',[84,77,69,79,72,59]),
  nominee('nom-va-1','Casey Turner','va',[81,83,71,84,68,62])
];
